import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Code,
  Flex,
  JsonInput,
  ScrollArea,
  Select,
  Space,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconCode, IconInfoCircle, IconRefresh, IconSend, IconX } from '@tabler/icons-react';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { DroxyEmptyStateBox } from 'components/Droxy/DroxyEmptyStateBox';
import { SectionNumber } from 'components/SectionNumber';
import { JsonSchemaBuilder } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/JsonSchemaBuilder';
import { SchemaUtils } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/utils';
import { KeyValueEditor } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/KeyValueEditor/KeyValueEditor';
import { useApiTester } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/useApiTester';
import { VariableInsertButton } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/VariableInsertButton';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES } from 'shared/constants/smart-assistant';
import { CustomApiRequestMethod, CustomApiRequestToolConfig } from 'shared/interfaces/tools/CustomApiRequest';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { formHasFieldErrors } from 'shared/utils/form';
import { noSpacesValidator, requiredInputValidator } from 'shared/utils/form-validators';

enum CustomApiRequestPanelSections {
  GENERAL_INFORMATION = 'general-information',
  PARAMETERS = 'parameters',
  API_CONFIGURATION = 'api-configuration',
  API_RESPONSE_INSTRUCTIONS = 'response-instructions',
  TEST_API = 'test-api'
}

interface CustomApiRequestToolConfigurationPanelProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
}

export function CustomApiRequestToolConfigurationPanel({
  onUpdate,
  onBack
}: CustomApiRequestToolConfigurationPanelProps) {
  const { t } = useTranslation();
  const [activeApiRequestConfigurationTab, setActiveApiRequestConfigurationTab] = useState<string>('headers');
  const location = useLocation();
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingToolConfig = toolConfigs.find((config) => config.kind === ToolKind.CUSTOM_API_REQUEST) as
    | CustomApiRequestToolConfig
    | undefined;
  const isNewToolConfig = !maybeExistingToolConfig;

  const customApiRequestToolConfigForm = useForm<CustomApiRequestToolConfig>({
    initialValues: {
      _id: maybeExistingToolConfig?._id,
      kind: ToolKind.CUSTOM_API_REQUEST as const,
      toolName: maybeExistingToolConfig?.toolName || DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES.toolName,
      enabled: maybeExistingToolConfig?.enabled ?? true,
      trigger: ToolTrigger.INTENT,
      description:
        maybeExistingToolConfig?.description || DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES.description,
      parametersJsonSchema:
        maybeExistingToolConfig?.parametersJsonSchema ||
        DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES.parametersJsonSchema,
      responseHandlingConfiguration:
        maybeExistingToolConfig?.responseHandlingConfiguration ||
        DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES.responseHandlingConfiguration,
      requestConfiguration:
        maybeExistingToolConfig?.requestConfiguration ||
        DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES.requestConfiguration
    },
    validate: {
      toolName: (value) => requiredInputValidator(value) || noSpacesValidator(value),
      description: (value) => requiredInputValidator(value),
      requestConfiguration: {
        urlTemplate: (value) => requiredInputValidator(value),
        method: (value) => requiredInputValidator(value)
      },
      responseHandlingConfiguration: {
        successInstruction: (value) => requiredInputValidator(value),
        errorInstruction: (value) => requiredInputValidator(value)
      }
    }
  });

  const apiTester = useApiTester({
    toolConfig: customApiRequestToolConfigForm.values
  });

  const isValidJson = useMemo(() => {
    const bodyTemplate = customApiRequestToolConfigForm.values.requestConfiguration?.bodyTemplate;
    if (!bodyTemplate || bodyTemplate.trim() === '') return null;
    try {
      JSON.parse(bodyTemplate);
      return true;
    } catch {
      return false;
    }
  }, [customApiRequestToolConfigForm.values.requestConfiguration?.bodyTemplate]);

  const handleUpdate = () => {
    const validation = customApiRequestToolConfigForm.validate();
    if (validation.hasErrors) return;
    onUpdate([customApiRequestToolConfigForm.values]);
  };

  const handleRenderRequest = () => {
    apiTester.renderRequest();
  };

  const handleExecuteRequest = () => {
    apiTester.executeRequest();
  };

  const handlePrefillTestParams = () => {
    const schemaString = customApiRequestToolConfigForm.values.parametersJsonSchema;
    if (schemaString) {
      const parsedSchema = SchemaUtils.parseJsonString(schemaString);
      if (parsedSchema) {
        const exampleParams = SchemaUtils.generateExampleFromSchema(parsedSchema);
        const jsonString = JSON.stringify(exampleParams, null, 2);
        apiTester.setTestParams(jsonString);
      }
    }
  };

  const shouldPreventApiRequestTesting = useMemo(() => {
    const values = customApiRequestToolConfigForm.values;
    return (
      !values.toolName?.trim() ||
      !values.description?.trim() ||
      !values.requestConfiguration?.urlTemplate?.trim() ||
      !values.responseHandlingConfiguration?.successInstruction?.trim() ||
      !values.responseHandlingConfiguration?.errorInstruction?.trim()
    );
  }, [
    customApiRequestToolConfigForm.values.toolName,
    customApiRequestToolConfigForm.values.description,
    customApiRequestToolConfigForm.values.requestConfiguration?.urlTemplate,
    customApiRequestToolConfigForm.values.responseHandlingConfiguration?.successInstruction,
    customApiRequestToolConfigForm.values.responseHandlingConfiguration?.errorInstruction
  ]);

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => customApiRequestToolConfigForm.reset()}
      hasChanges={customApiRequestToolConfigForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={customApiRequestToolConfigForm.values.enabled}
          onToggle={(enabled) => customApiRequestToolConfigForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Box>
        <DroxyAccordion defaultOpenSections={[CustomApiRequestPanelSections.GENERAL_INFORMATION]}>
          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.GENERAL_INFORMATION')}
            section={CustomApiRequestPanelSections.GENERAL_INFORMATION}
            icon={<SectionNumber number={1} />}
            hasError={formHasFieldErrors(customApiRequestToolConfigForm, 'toolName', 'description')}>
            <Stack spacing="md">
              <TextInput
                label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TOOL_NAME.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TOOL_NAME.PLACEHOLDER')}
                maxLength={50}
                onKeyDown={(event) => {
                  if (event.key === ' ') event.preventDefault();
                }}
                {...customApiRequestToolConfigForm.getInputProps('toolName')}
              />
              <Textarea
                label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.WHEN_TO_USE.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.WHEN_TO_USE.PLACEHOLDER')}
                maxLength={500}
                minRows={3}
                autosize
                {...customApiRequestToolConfigForm.getInputProps('description')}
              />
            </Stack>
          </DroxyAccordionSection>

          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.PARAMETERS')}
            section={CustomApiRequestPanelSections.PARAMETERS}
            icon={<SectionNumber number={2} />}
            hasError={formHasFieldErrors(customApiRequestToolConfigForm, 'parametersJsonSchema')}>
            <Stack spacing="md">
              <JsonSchemaBuilder
                value={customApiRequestToolConfigForm.values.parametersJsonSchema}
                onChange={(schema) => customApiRequestToolConfigForm.setFieldValue('parametersJsonSchema', schema)}
              />
            </Stack>
          </DroxyAccordionSection>

          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_CONFIGURATION.TITLE')}
            section={CustomApiRequestPanelSections.API_CONFIGURATION}
            icon={<SectionNumber number={3} />}
            hasError={formHasFieldErrors(customApiRequestToolConfigForm, 'requestConfiguration')}>
            <Stack spacing="md">
              <Alert color="blue" variant="light" icon={<IconInfoCircle />}>
                <Text size="sm" fw={500}>
                  {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_CONFIGURATION.ALERT.TITLE')}
                </Text>
                <Space h={4} />
                <Text size="xs">{t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_CONFIGURATION.ALERT.TEXT')}</Text>
              </Alert>

              <Flex gap="md" align="end">
                <Select
                  label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.HTTP_METHOD.LABEL')}
                  data={[
                    { value: CustomApiRequestMethod.GET, label: 'GET' },
                    { value: CustomApiRequestMethod.POST, label: 'POST' },
                    { value: CustomApiRequestMethod.PUT, label: 'PUT' },
                    { value: CustomApiRequestMethod.PATCH, label: 'PATCH' },
                    { value: CustomApiRequestMethod.DELETE, label: 'DELETE' }
                  ]}
                  style={{ minWidth: 140 }}
                  {...customApiRequestToolConfigForm.getInputProps('requestConfiguration.method')}
                />

                <TextInput
                  label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.URL.LABEL')}
                  placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.URL.PLACEHOLDER')}
                  style={{ flex: 1 }}
                  rightSection={
                    <VariableInsertButton
                      jsonSchema={customApiRequestToolConfigForm.values.parametersJsonSchema}
                      onInsertVariable={(variable) => {
                        const currentValue =
                          customApiRequestToolConfigForm.values.requestConfiguration?.urlTemplate || '';
                        customApiRequestToolConfigForm.setFieldValue(
                          'requestConfiguration.urlTemplate',
                          currentValue + `{{${variable}}}`
                        );
                      }}
                    />
                  }
                  rightSectionProps={{ style: { pointerEvents: 'all' } }}
                  {...customApiRequestToolConfigForm.getInputProps('requestConfiguration.urlTemplate')}
                />
              </Flex>

              <Tabs
                value={activeApiRequestConfigurationTab}
                onTabChange={(value) => value && setActiveApiRequestConfigurationTab(value)}>
                <Tabs.List>
                  <Tabs.Tab value="headers">{t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.HEADERS')}</Tabs.Tab>
                  <Tabs.Tab value="query">{t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.QUERY_PARAMETERS')}</Tabs.Tab>
                  <Tabs.Tab value="body">{t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.BODY')}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="headers" pt="md" style={{ minHeight: 200 }}>
                  <KeyValueEditor
                    value={customApiRequestToolConfigForm.values.requestConfiguration.headersTemplate}
                    onChange={(headers) =>
                      customApiRequestToolConfigForm.setFieldValue('requestConfiguration.headersTemplate', headers)
                    }
                    label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.HEADERS')}
                    placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.NO_HEADERS_ADDED')}
                    keyPlaceholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.HEADER_NAME_PLACEHOLDER')}
                    valuePlaceholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.HEADER_VALUE_PLACEHOLDER')}
                    variableJsonSchema={customApiRequestToolConfigForm.values.parametersJsonSchema}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="query" pt="md" style={{ minHeight: 200 }}>
                  <KeyValueEditor
                    value={customApiRequestToolConfigForm.values.requestConfiguration.queryParametersTemplate}
                    onChange={(queryParams) =>
                      customApiRequestToolConfigForm.setFieldValue(
                        'requestConfiguration.queryParametersTemplate',
                        queryParams
                      )
                    }
                    label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.QUERY_PARAMETERS')}
                    placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.NO_QUERY_PARAMETERS_ADDED')}
                    keyPlaceholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.PARAMETER_NAME_PLACEHOLDER')}
                    valuePlaceholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.PARAMETER_VALUE_PLACEHOLDER')}
                    variableJsonSchema={customApiRequestToolConfigForm.values.parametersJsonSchema}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="body" pt="md" style={{ minHeight: 200 }}>
                  <Box>
                    <Flex justify="space-between" align="center" mb="md" h={20}>
                      <Text size="sm" fw={500} c="dark">
                        {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.BODY')}
                      </Text>
                      <Box h={16}>
                        {isValidJson === true && (
                          <Flex align="center" gap={4}>
                            <IconCheck size={14} style={{ flexShrink: 0 }} color="green" />
                            <Text size="xs" c="green" fw={500} style={{ lineHeight: 1 }}>
                              {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.VALID_JSON')}
                            </Text>
                          </Flex>
                        )}
                        {isValidJson === false && (
                          <Flex align="center" gap={4}>
                            <IconX size={14} style={{ flexShrink: 0 }} color="red" />
                            <Text size="xs" c="red" fw={500} style={{ lineHeight: 1 }}>
                              {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.INVALID_JSON')}
                            </Text>
                          </Flex>
                        )}
                      </Box>
                    </Flex>
                    <JsonInput
                      placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_CONFIGURATION.BODY_PLACEHOLDER')}
                      rightSection={
                        <VariableInsertButton
                          jsonSchema={customApiRequestToolConfigForm.values.parametersJsonSchema}
                          onInsertVariable={(variable) => {
                            const currentValue =
                              customApiRequestToolConfigForm.values.requestConfiguration?.bodyTemplate || '';
                            customApiRequestToolConfigForm.setFieldValue(
                              'requestConfiguration.bodyTemplate',
                              currentValue + `{{${variable}}}`
                            );
                          }}
                        />
                      }
                      rightSectionProps={{
                        style: {
                          pointerEvents: 'all',
                          alignItems: 'flex-start',
                          paddingTop: '8px'
                        }
                      }}
                      {...customApiRequestToolConfigForm.getInputProps('requestConfiguration.bodyTemplate')}
                    />
                  </Box>
                </Tabs.Panel>
              </Tabs>
            </Stack>
          </DroxyAccordionSection>

          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_RESPONSE_INSTRUCTIONS')}
            section={CustomApiRequestPanelSections.API_RESPONSE_INSTRUCTIONS}
            icon={<SectionNumber number={4} />}
            hasError={formHasFieldErrors(customApiRequestToolConfigForm, 'responseHandlingConfiguration')}>
            <Stack spacing="md">
              <Textarea
                label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.SUCCESS_RESPONSE_INSTRUCTION.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.SUCCESS_RESPONSE_INSTRUCTION.PLACEHOLDER')}
                maxLength={1000}
                minRows={3}
                autosize
                {...customApiRequestToolConfigForm.getInputProps('responseHandlingConfiguration.successInstruction')}
              />
              <Textarea
                label={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.ERROR_RESPONSE_INSTRUCTION.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.ERROR_RESPONSE_INSTRUCTION.PLACEHOLDER')}
                maxLength={1000}
                minRows={3}
                autosize
                {...customApiRequestToolConfigForm.getInputProps('responseHandlingConfiguration.errorInstruction')}
              />
            </Stack>
          </DroxyAccordionSection>

          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TEST_API')}
            section={CustomApiRequestPanelSections.TEST_API}
            icon={<SectionNumber number={5} />}
            hasError={false}>
            <Stack spacing="md">
              {shouldPreventApiRequestTesting ? (
                <DroxyEmptyStateBox message={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.COMPLETE_PREVIOUS_SECTIONS')}>
                  <Flex gap="xs">
                    <Button leftIcon={<IconCode size={14} />} variant="outline" disabled size="sm">
                      {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.RENDER_REQUEST')}
                    </Button>
                    <Button leftIcon={<IconSend size={14} />} disabled size="sm">
                      {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.EXECUTE_REQUEST')}
                    </Button>
                  </Flex>
                </DroxyEmptyStateBox>
              ) : (
                <>
                  <Box>
                    <Flex justify="space-between" align="center" mb="md" h={20}>
                      <Text size="sm" fw={500} c="dark">
                        {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TEST_PARAMETERS.LABEL')}
                      </Text>
                      <Flex align="center" gap="xs">
                        {customApiRequestToolConfigForm.values.parametersJsonSchema && (
                          <Button
                            variant="subtle"
                            size="xs"
                            leftIcon={<IconRefresh size={12} />}
                            onClick={handlePrefillTestParams}
                            c="dimmed">
                            {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.PREFILL_WITH_DEFAULTS')}
                          </Button>
                        )}
                        <Box h={16}>
                          {apiTester.testParamsValidation.isValid === true && (
                            <Flex align="center" gap={4}>
                              <IconCheck size={14} style={{ flexShrink: 0 }} color="green" />
                              <Text size="xs" c="green" fw={500} style={{ lineHeight: 1 }}>
                                {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.VALID_JSON')}
                              </Text>
                            </Flex>
                          )}
                          {apiTester.testParamsValidation.isValid === false && (
                            <Flex align="center" gap={4}>
                              <IconX size={14} style={{ flexShrink: 0 }} color="red" />
                              <Text size="xs" c="red" fw={500} style={{ lineHeight: 1 }}>
                                {apiTester.testParamsValidation.error}
                              </Text>
                            </Flex>
                          )}
                        </Box>
                      </Flex>
                    </Flex>
                    <JsonInput
                      styles={{ input: { minHeight: 200 } }}
                      value={apiTester.testParams}
                      onChange={apiTester.setTestParams}
                      placeholder={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TEST_PARAMETERS.PLACEHOLDER')}
                      description={t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.TEST_PARAMETERS.DESCRIPTION')}
                    />
                  </Box>

                  <Flex gap="xs">
                    <Button
                      leftIcon={<IconCode size={14} />}
                      variant="outline"
                      disabled={!apiTester.testParamsValidation.isValid}
                      onClick={handleRenderRequest}
                      size="sm">
                      {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.RENDER_REQUEST')}
                    </Button>
                    {apiTester.hasRendered && apiTester.renderedRequest && (
                      <Button
                        leftIcon={<IconSend size={14} />}
                        onClick={handleExecuteRequest}
                        loading={apiTester.isExecuting}
                        size="sm">
                        {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.EXECUTE_REQUEST')}
                      </Button>
                    )}
                  </Flex>
                </>
              )}

              {apiTester.hasRendered && apiTester.renderedRequest && (
                <Box>
                  <Flex align="center" gap={8} mb="md">
                    <IconCode size={16} color="gray" />
                    <Text size="sm" fw={500} c="dark">
                      {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.GENERATED_REQUEST')}
                    </Text>
                  </Flex>
                  <Code block bg="gray.0" p="md" style={{ whiteSpace: 'pre-wrap', fontSize: '13px' }}>
                    {`${apiTester.renderedRequest.method} ${apiTester.renderedRequest.url}${
                      apiTester.renderedRequest.params && Object.keys(apiTester.renderedRequest.params).length > 0
                        ? '?' +
                          new URLSearchParams(apiTester.renderedRequest.params as Record<string, string>).toString()
                        : ''
                    }${
                      apiTester.renderedRequest.headers && Object.keys(apiTester.renderedRequest.headers).length > 0
                        ? `\n\nHeaders:\n${Object.entries(apiTester.renderedRequest.headers)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('\n')}`
                        : ''
                    }${apiTester.renderedRequest.data ? `\n\nData:\n${apiTester.renderedRequest.data}` : ''}`}
                  </Code>
                </Box>
              )}

              {apiTester.apiResponse && (
                <Box>
                  <Flex align="center" gap={8} mb="md">
                    <Text size="sm" fw={500} c="dark">
                      {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.API_RESPONSE')}
                    </Text>
                    {apiTester.apiResponse.startsWith('Status: 2') && (
                      <Flex align="center" gap={4}>
                        <IconCheck size={14} style={{ flexShrink: 0 }} color="green" />
                        <Text size="xs" c="green" fw={500}>
                          {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.SUCCESS')}
                        </Text>
                      </Flex>
                    )}
                    {apiTester.apiResponse.startsWith('Status: 3') && (
                      <Flex align="center" gap={4}>
                        <IconCode size={14} style={{ flexShrink: 0 }} color="orange" />
                        <Text size="xs" c="orange" fw={500}>
                          {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.REDIRECT')}
                        </Text>
                      </Flex>
                    )}
                    {(apiTester.apiResponse.startsWith('Status: 4') ||
                      apiTester.apiResponse.startsWith('Status: 5')) && (
                      <Flex align="center" gap={4}>
                        <IconX size={14} style={{ flexShrink: 0 }} color="red" />
                        <Text size="xs" c="red" fw={500}>
                          {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.ERROR')}
                        </Text>
                      </Flex>
                    )}
                    {apiTester.apiResponse.startsWith('Error:') && (
                      <Flex align="center" gap={4}>
                        <IconX size={14} style={{ flexShrink: 0 }} color="red" />
                        <Text size="xs" c="red" fw={500}>
                          {t('SMART_ASSISTANT.TOOLS.CUSTOM_API_REQUEST.FAILED')}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                  <ScrollArea h={300}>
                    <Code block bg="gray.0" p="md" style={{ whiteSpace: 'pre-wrap', fontSize: '13px' }}>
                      {apiTester.apiResponse}
                    </Code>
                  </ScrollArea>
                </Box>
              )}
            </Stack>
          </DroxyAccordionSection>
        </DroxyAccordion>
      </Box>
    </ToolConfigurationPanelWrapper>
  );
}
